import React from 'react'
import {Field, getIn, useFormikContext} from 'formik'
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  PinInput,
  PinInputField,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Text,
  Tooltip,
  Textarea
} from '@chakra-ui/react'
import {InfoOutlineIcon} from '@chakra-ui/icons'
import {NumericFormat} from 'react-number-format'
import {CountrySelector, usePhoneInput} from 'react-international-phone'
import 'react-international-phone/style.css'
import moment from 'moment'
import {FileInputField} from './file-input-field'
import {AddressInputField} from './form-fields/address-input-field'
import {AddressInputsFields} from './form-fields/address-inputs-fields'
import {IdFields} from './form-fields/id-fields'
import {Autocomplete} from './autocomplete'

export interface FieldInputProps {
  name: string
  label?: string
  description?: string
  placeholder?: string
  buttonText?: string
  componentType:
    | 'input'
    | 'select'
    | 'number-input'
    | 'phone-input'
    | 'switch'
    | 'date-input'
    | 'radio-group'
    | 'button-group'
    | 'file-input'
    | 'checkbox-group'
    | 'checkbox'
    | 'textarea'
    | 'address-input'
    | 'address-inputs'
    | 'id-fields'
    | 'autocomplete'
    | 'pin-input'
  options?: {value: string; label: string; readonly?: boolean}[]
  disabled?: boolean
  handleBlur?: (e: any) => void
  handleChange?: (e: any) => void
  type?: string
  readOnly?: boolean
  inputRightElement?: React.ReactNode
  numberFormat?: {
    max?: number
    min?: number
  }
  size?: 'sm' | 'md' | 'lg'
  decimalScale?: number
  hasMultipleFiles?: boolean
  handleLoad?: (autocomplete: any) => void
  handlePlaceChanged?: () => void
  direction?: 'row' | 'column'
  layout?: 'default' | 'horizontal'
  pinLength?: number
}

const filledFieldProps = {
  bg: 'white',
  borderWidth: '1px',
  borderColor: 'gray.200',
  boxShadow: 'sm',
  _hover: {boxShadow: 'sm', borderColor: 'gray.300'},
  _focusVisible: {
    bg: 'white',
    borderColor: 'primary2.500',
    boxShadow: '0 0 0 1px var(--chakra-colors-primary2-500), var(--chakra-shadows-sm)'
  },
  _disabled: {
    bg: 'gray.100',
    borderColor: 'gray.200',
    boxShadow: 'none'
  }
}

export const FieldInput: React.FC<FieldInputProps> = ({
  name,
  label,
  description,
  componentType,
  options,
  type,
  inputRightElement,
  numberFormat,
  handleBlur,
  handleChange = () => {
    // do something
  },
  placeholder,
  disabled,
  size = 'sm',
  decimalScale = 2,
  hasMultipleFiles = false,
  direction = 'column',
  layout = 'default',
  pinLength = 6,
  ...props
}) => {
  const {errors, touched} = useFormikContext<any>() || {errors: {}, touched: {}}

  const renderLabelWithDescriptionTooltip = () => (
    <HStack align="center" spacing={1} mb={2}>
      {label && (
        <FormLabel my={0} htmlFor={name} fontSize="11px" fontWeight="semibold" color="gray.700">
          {label}
        </FormLabel>
      )}
      {!!description && (
        <Tooltip label={description} hasArrow placement="top-start">
          <Box as="span" display="inline-flex" alignItems="center" mt={0.5} color="gray.500" cursor="help">
            <InfoOutlineIcon boxSize={3.5} />
          </Box>
        </Tooltip>
      )}
    </HStack>
  )

  return (
    <FormControl isInvalid={!!(typeof getIn(errors, name) === 'string' && getIn(touched, name))} display="block" alignItems="center" gap={4}>
      <Field name={name}>
        {({field, form}: {field: any; form: any}) => {
          if (componentType === 'switch' && layout === 'horizontal') {
            return (
              <HStack spacing={4} width="100%" justify="space-between">
                {renderLabelWithDescriptionTooltip()}
                <Switch
                  id={name}
                  isChecked={field.value}
                  {...field}
                  {...props}
                  onChange={(e) => {
                    handleChange(e)
                    field?.onChange(e)
                  }}
                  isDisabled={disabled}
                  size={size || 'lg'}
                />
              </HStack>
            )
          }

          return (
            <>
              {renderLabelWithDescriptionTooltip()}
              {componentType === 'input' && (
                <InputGroup>
                  <Input
                    type={type}
                    {...field}
                    onChange={(e) => {
                      handleChange(e)
                      field?.onChange(e)
                    }}
                    value={field.value || ''}
                    placeholder={placeholder || label}
                    isDisabled={disabled}
                    size={size}
                    {...filledFieldProps}
                  />
                  {inputRightElement && <InputRightElement width="4.5rem">{inputRightElement}</InputRightElement>}
                </InputGroup>
              )}
              {componentType === 'autocomplete' && (
                <Autocomplete
                  {...field}
                  options={options || []}
                  value={field.value}
                  onChange={(value) => {
                    const syntheticEvent = {
                      target: {
                        name: field.name,
                        value
                      }
                    }
                    handleChange(syntheticEvent)
                    field.onChange(syntheticEvent)
                  }}
                  isDisabled={disabled}
                />
              )}
              {componentType === 'textarea' && (
                <Textarea
                  {...field}
                  onChange={(e) => {
                    handleChange(e)
                    field?.onChange(e)
                  }}
                  value={field.value || ''}
                  placeholder={placeholder || label}
                  isDisabled={disabled}
                  size={size}
                  {...filledFieldProps}
                />
              )}

              {componentType === 'switch' && (
                <Switch
                  id={name}
                  isChecked={field.value}
                  {...field}
                  {...props}
                  onChange={(e) => {
                    handleChange(e)
                    field?.onChange(e)
                  }}
                  isDisabled={disabled}
                  size={size || 'lg'}
                />
              )}
              {componentType === 'phone-input' && (
                <PhoneInput
                  field={field}
                  handleBlur={handleBlur}
                  handleChange={(e) => {
                    handleChange(e)
                    field.onChange(e)
                  }}
                  isDisabled={disabled}
                  size={size}
                />
              )}
              {componentType === 'date-input' && (
                <InputGroup>
                  <Input
                    {...field}
                    value={field.value ? moment(field.value).format('YYYY-MM-DD') : ''}
                    type="date"
                    onChange={(e) => {
                      handleChange(e)
                      field.onChange(e)
                    }}
                    isDisabled={disabled}
                    size={size}
                    {...filledFieldProps}
                  />
                </InputGroup>
              )}

              {componentType === 'number-input' && (
                <InputGroup>
                  <Input
                    as={NumericFormat}
                    thousandSeparator=","
                    decimalSeparator="."
                    decimalScale={decimalScale}
                    isAllowed={(values: any) => {
                      let isValid = true
                      if ((numberFormat?.max && values.floatValue > numberFormat.max) || (numberFormat?.min && values.floatValue < numberFormat?.min)) {
                        isValid = false
                      }

                      return isValid
                    }}
                    {...field}
                    onChange={(e) => {
                      handleChange(e)
                      field?.onChange(e)
                    }}
                    isDisabled={disabled}
                    {...filledFieldProps}
                  />
                  {inputRightElement && <InputRightElement width="4.5rem">{inputRightElement}</InputRightElement>}
                </InputGroup>
              )}

              {componentType === 'radio-group' && (
                <RadioGroup
                  {...field}
                  value={String(field.value ?? '')}
                  isDisabled={disabled}
                  onChange={(value) => {
                    const event = {
                      target: {
                        name: field.name,
                        value
                      }
                    }
                    handleChange(event)
                    field.onChange(event)
                  }}
                >
                  <Stack direction={direction} spacing={2} align="stretch">
                    {options?.map(({label, value}, idx) => {
                      const isChecked = String(field.value) === value
                      return (
                        <Box
                          as="label"
                          key={`${label}-${idx}`}
                          flex={direction === 'row' ? 1 : undefined}
                          px={3}
                          py={2}
                          rounded="md"
                          borderWidth="1px"
                          borderColor={isChecked ? 'primary2.300' : 'gray.200'}
                          bg={isChecked ? 'primary2.50' : 'white'}
                          boxShadow="sm"
                          cursor="pointer"
                          _hover={{borderColor: isChecked ? 'primary2.300' : 'gray.300', boxShadow: 'sm'}}
                        >
                          <Radio
                            value={value}
                            size="sm"
                            colorScheme="primary2"
                            sx={{
                              '.chakra-radio__control': {
                                bg: 'white',
                                borderColor: 'gray.300',
                                boxShadow: 'sm',
                                _hover: {boxShadow: 'sm'},
                                _checked: {
                                  bg: 'primary2.500',
                                  borderColor: 'primary2.500'
                                }
                              },
                              '.chakra-radio__label': {
                                fontSize: 'sm',
                                fontWeight: 'medium',
                                color: 'gray.800'
                              }
                            }}
                          >
                            {label}
                          </Radio>
                        </Box>
                      )
                    })}
                  </Stack>
                </RadioGroup>
              )}
              {componentType === 'button-group' && (
                <Box mb={4}>
                  <ButtonGroup isAttached variant="outline" width="100%" size="sm">
                    {options?.map(({label, value}, idx) => (
                      <Button
                        key={`${label}-${idx}`}
                        flex={1}
                        variant={field.value === value ? 'solid' : 'ghost'}
                        colorScheme={field.value === value ? 'primary2' : undefined}
                        color={field.value === value ? undefined : 'whiteAlpha.900'}
                        bg={field.value === value ? undefined : 'whiteAlpha.50'}
                        _hover={field.value === value ? undefined : {bg: 'whiteAlpha.100'}}
                        onClick={() => {
                          const event = {
                            target: {name: field.name, value}
                          }
                          handleChange(event)
                          field.onChange(event)
                        }}
                        isDisabled={disabled}
                      >
                        {label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              )}
              {componentType === 'checkbox' && (
                <Checkbox
                  {...field}
                  isChecked={!!field.value}
                  size="sm"
                  colorScheme="primary2"
                  px={3}
                  py={2}
                  rounded="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                  bg="white"
                  boxShadow="sm"
                  _hover={{borderColor: 'gray.300', boxShadow: 'sm'}}
                  isDisabled={disabled}
                  onChange={(e) => {
                    handleChange(e)
                    field.onChange(e)
                  }}
                  sx={{
                    '&[data-checked]': {
                      bg: 'primary2.50',
                      borderColor: 'primary2.300'
                    },
                    '.chakra-checkbox__control': {
                      bg: 'white',
                      borderColor: 'gray.300',
                      boxShadow: 'sm',
                      w: '14px',
                      h: '14px',
                      minW: '14px',
                      '& svg': {
                        fontSize: '10px'
                      },
                      _hover: {boxShadow: 'sm'},
                      _checked: {
                        bg: 'primary2.500',
                        borderColor: 'primary2.500'
                      }
                    },
                    '.chakra-checkbox__label': {
                      fontSize: 'sm',
                      fontWeight: 'medium',
                      color: 'gray.800'
                    }
                  }}
                >
                  {placeholder}
                </Checkbox>
              )}

              {componentType === 'checkbox-group' && (
                <Stack direction="column" spacing={2}>
                  {options?.map(({label, value}, idx) => {
                    const selectedValues: string[] = Array.isArray(field.value) ? field.value : []
                    const isChecked = selectedValues.includes(value)

                    return (
                      <Checkbox
                        key={`${label}-${idx}`}
                        name={field.name}
                        value={value}
                        isChecked={isChecked}
                        isDisabled={disabled}
                        onChange={(e) => {
                          const nextValue = e.target.checked ? [...selectedValues, value] : selectedValues.filter((selectedValue) => selectedValue !== value)
                          const event = {
                            target: {
                              name: field.name,
                              value: nextValue
                            }
                          }

                          form.setFieldTouched(field.name, true, false)
                          void form.setFieldValue(field.name, nextValue).then(() => handleChange(event))
                        }}
                        size="sm"
                        colorScheme="primary2"
                        px={3}
                        py={2}
                        rounded="md"
                        borderWidth="1px"
                        borderColor="gray.200"
                        bg="white"
                        boxShadow="sm"
                        _hover={{borderColor: 'gray.300', boxShadow: 'sm'}}
                        sx={{
                          '&[data-checked]': {
                            bg: 'primary2.50',
                            borderColor: 'primary2.300'
                          },
                          '.chakra-checkbox__control': {
                            bg: 'white',
                            borderColor: 'gray.300',
                            boxShadow: 'sm',
                            w: '14px',
                            h: '14px',
                            minW: '14px',
                            '& svg': {
                              fontSize: '10px'
                            },
                            _hover: {boxShadow: 'sm'},
                            _checked: {
                              bg: 'primary2.500',
                              borderColor: 'primary2.500'
                            }
                          },
                          '.chakra-checkbox__label': {
                            fontSize: 'sm',
                            fontWeight: 'medium',
                            color: 'gray.800'
                          }
                        }}
                      >
                        {label}
                      </Checkbox>
                    )
                  })}
                </Stack>
              )}

              {componentType === 'select' && (
                <Select
                  {...field}
                  onChange={(e) => {
                    // create a custom event to pass the name and value
                    console.debug(`Select onChange`, e)
                    handleChange(e)
                    field.onChange(e)
                  }}
                  value={field.value || ''}
                  isDisabled={disabled}
                  size={size}
                  {...filledFieldProps}
                >
                  <option></option>
                  {options?.map((option) => {
                    return (
                      <option key={`option-${option.value}`} value={option.value}>
                        {option.label}
                      </option>
                    )
                  })}
                </Select>
              )}
              {componentType === 'file-input' && (
                <FileInputField
                  hasMultiple={hasMultipleFiles}
                  size={size}
                  isDisabled={disabled}
                  {...field}
                  onChange={(e) => {
                    handleChange(e)
                    field.onChange(e)
                  }}
                />
              )}
              {componentType === 'address-input' && <AddressInputField disabled={disabled} {...field} {...props} />}
              {componentType === 'address-inputs' && (
                <AddressInputsFields
                  disabled={disabled}
                  handleChange={(e: any) => {
                    handleChange(e)
                    field.onChange(e)
                  }}
                  {...field}
                />
              )}
              {componentType === 'id-fields' && (
                <IdFields
                  disabled={disabled}
                  handleChange={(e: any) => {
                    handleChange(e)
                    field.onChange(e)
                  }}
                  {...field}
                />
              )}
              {componentType === 'pin-input' && (
                <HStack spacing={2} justify="center">
                  <PinInput
                    value={field.value || ''}
                    onChange={(value) => {
                      const event = {
                        target: {
                          name: field.name,
                          value
                        }
                      }
                      handleChange(event)
                      field.onChange(event)
                    }}
                    otp
                    size={size as any}
                    isDisabled={disabled}
                  >
                    {Array.from({length: pinLength}).map((_, i) => (
                      <PinInputField key={i} />
                    ))}
                  </PinInput>
                </HStack>
              )}
            </>
          )
        }}
      </Field>
      {typeof getIn(errors, name) === 'string' && <FormErrorMessage>{getIn(errors, name)}</FormErrorMessage>}
    </FormControl>
  )
}

interface PhoneInputProps {
  field: any
  handleBlur?: (e: any) => void
  handleChange?: (e: any) => void
  isDisabled?: boolean
  size?: 'sm' | 'md' | 'lg'
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const PhoneInput = ({field, handleBlur, handleChange = () => {}, isDisabled, size = 'sm'}: PhoneInputProps) => {
  const {setFieldValue, values} = useFormikContext<any>()

  const phoneInput = usePhoneInput({
    defaultCountry: 'ca',
    value: getIn(values, field.name),
    onChange: (value) => {
      // this is a hack to fix the issue with the phone input not being able to handle the value on the first render
      // TODO Adnan: find a better solution
      try {
        setFieldValue(field.name, value.phone)
      } catch (e) {
        // do nothing
      }
    }
  })

  return (
    <Box display="flex" gap={1}>
      <CountrySelector
        selectedCountry={phoneInput.country}
        onSelect={(country) => phoneInput.setCountry(country.iso2)}
        renderButtonWrapper={({children, rootProps}) => (
          <Button
            {...rootProps}
            variant="ghost"
            size={size}
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            boxShadow="sm"
            _hover={{bg: 'white', boxShadow: 'sm', borderColor: 'gray.300'}}
            _active={{bg: 'white'}}
            _disabled={{bg: 'gray.100', borderColor: 'gray.200', boxShadow: 'none'}}
          >
            {children}
          </Button>
        )}
        disabled={isDisabled}
      />
      <Input
        placeholder="Phone number"
        type="tel"
        color="primary"
        ref={phoneInput.inputRef}
        {...field}
        value={phoneInput?.phone || ''}
        onChange={phoneInput.handlePhoneValueChange}
        onBlur={handleBlur}
        isDisabled={isDisabled}
        size={size}
        {...filledFieldProps}
      />
    </Box>
  )
}
